import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";

interface RequestRevisionDialogProps {
  open: boolean;
  onSubmit: (reason: string) => Promise<void>;
  onClose: () => void;
  loading: boolean;
}

const RequestRevisionDialog: React.FC<RequestRevisionDialogProps> = ({
  open,
  onSubmit,
  onClose,
  loading,
}) => {
  useRedesignSetup();
  const { translate } = useTranslation(["servicing", "common"]);
  const [reason, setReason] = useState("");
  const [error, setError] = useState(false);

  // Inject dialog keyframes
  useEffect(() => {
    const styleId = "request-revision-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes revisionDialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError(true);
      return;
    }
    setError(false);
    await onSubmit(reason.trim());
    setReason("");
  };

  const handleClose = () => {
    setReason("");
    setError(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          overflow: "hidden",
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "revisionDialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
    >
      {/* CENTERED HEADER — orange circled icon + title + subtitle */}
      <DialogTitle sx={{ textAlign: "center", pt: 3.5, pb: 0.5, px: 3 }}>
        {/* Circled icon — orange/warning intent */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "#FEF9E7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 1.5,
          }}
        >
          <ReplayOutlinedIcon sx={{ fontSize: 26, color: "#F39C12" }} />
        </Box>

        {/* Title */}
        <Box
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            fontFamily: dt.font,
            color: dt.textPrimary,
            letterSpacing: "-0.3px",
          }}
        >
          {translate("servicing:completion.revisionTitle")}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
        {/* Subtitle — centered */}
        <Box
          sx={{
            fontSize: "13.5px",
            fontFamily: dt.font,
            color: dt.textSecondary,
            textAlign: "center",
            lineHeight: 1.5,
            mb: 2.5,
          }}
        >
          {translate("servicing:completion.revisionDescription")}
        </Box>

        {/* Section label: REVISION REASON * */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: 2.5 }}>
          <SubjectOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />
          <Box
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              fontFamily: dt.font,
              textTransform: "uppercase",
              letterSpacing: "1px",
              color: dt.textPrimary,
            }}
          >
            {translate("servicing:completion.revisionReason")}
            <span style={{ color: "#C0392B", marginLeft: 4 }}>*</span>
          </Box>
        </Box>

        {/* Native textarea — gray bg, no border, blue focus ring, resizable */}
        <Box
          sx={{
            background: dt.inputBg,
            borderRadius: dt.radiusSm,
            border: error ? "1.5px solid #C0392B" : "1.5px solid transparent",
            padding: "12px",
            transition: "all 0.2s ease",
            "&:hover": { backgroundColor: "#EDF0F5" },
            "&:focus-within": {
              backgroundColor: "#FFFFFF",
              border: error ? "1.5px solid #C0392B" : `1.5px solid ${dt.blue}`,
              boxShadow: error ? "none" : `0 0 0 3px ${dt.blueGlow}`,
            },
          }}
        >
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value.trim()) setError(false);
            }}
            placeholder={translate("servicing:completion.revisionReasonPlaceholder")}
            maxLength={2000}
            rows={5}
            style={{
              border: "none",
              outline: "none",
              width: "100%",
              fontSize: "14px",
              fontFamily: 'var(--font-sans)',
              color: "#1a1a2e",
              background: "transparent",
              resize: "vertical",
              minHeight: "120px",
              lineHeight: 1.6,
              boxSizing: "border-box",
            }}
          />
        </Box>
        {error && (
          <Box
            sx={{
              fontSize: "12px",
              fontFamily: dt.font,
              color: "#C0392B",
              mt: 0.5,
              ml: 1.5,
            }}
          >
            {translate("servicing:completion.revisionReasonRequired")}
          </Box>
        )}
      </DialogContent>

      {/* ACTIONS — centered per Dialog Standard */}
      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.5 }}>
        {/* Cancel — text button */}
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            color: dt.blue,
            padding: "8px 24px",
            borderRadius: dt.radiusSm,
            "&:hover": { backgroundColor: dt.blueSoft },
          }}
        >
          {translate("common:buttons.cancel")}
        </Button>

        {/* Submit — filled button with icon */}
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
            ) : (
              <ReplayOutlinedIcon sx={{ fontSize: "18px !important" }} />
            )
          }
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "#F39C12",
            padding: "10px 28px",
            borderRadius: dt.radiusSm,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#D97706",
              boxShadow: "0 2px 8px rgba(243,156,18,0.3)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {loading
            ? translate("servicing:completion.submittingRevision")
            : translate("servicing:completion.submitRevision")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestRevisionDialog;
